import { useCallback, useEffect, useState } from 'react';

import { GLI, getEnv } from '@/data';
import { initAppsFlyer } from '@/data/appsflyer';
import { initBraze, requestNotificationPermission } from '@/data/braze';
import { useInitIntercom } from '@/data/intercom/intercom';
import { getGeocomplyClient } from '@/data/location/geocomply-common';
import BetrAnalytics from '@/feature/analytics/analytics';
import { AnalyticsUserProperty } from '@/feature/analytics/constants';
import { initClarity } from '@/feature/analytics/hooks/use-clarity';
import { useFetchAndActivateFirebaseRemoteConfigs } from '@/feature/analytics/hooks/use-firebase-remote-config';
import { useRudderStack } from '@/feature/analytics/hooks/use-rudder-stack';
import { cacheMarketingIdentity } from '@/feature/analytics/marketingIdentity';
import { Segmentation } from '@/feature/analytics/segmentation';
import { useUpdatePickemLeagueConfigs } from '@/feature/betslip-pickem/hooks/use-league-configs-store';
import { useGuestCacheInvalidation } from '@/feature/guest-user/hooks/use-guest-cache-invalidation';
import { useMakeFirstDeposit } from '@/feature/lobby/hooks/use-make-first-deposit';
import { usePromptBiometricGuard } from '@/feature/lobby/hooks/use-prompt-biometric-guard';
import { useBiometricCheck } from '@/utils/biometrics';
import { logger } from '@/utils/logging';

import { useAppVersionCheck } from './use-app-version-check';
import { useAuthUserConfig, userIdSelector } from './use-auth-user-config';
import { useJurisdictionStore } from './use-jurisdiction';
import { useMaintenanceCheck } from './use-maintenance-check';
import { useAppResumeEffect } from './use-resume';
import { useTrackingEnabled } from './use-tracking-enabled';
import { useUserStore } from './use-user-store';

/**
 * Hook that runs the initialization of the app SDKs.
 */
export const useAppSdksInit = () => {
    const ENV = getEnv();
    const initIntercom = useInitIntercom();
    const userId = useUserStore(state => state.profile?.sub);

    useTrackingEnabled();
    initClarity();

    //Marketing tech SDKs should be disabled for GLI variant
    const marTechEnabled = ENV !== GLI;
    if (marTechEnabled) {
        initAppsFlyer();
        initBraze();
        initIntercom();
    }
    const isRudderStackReady = useRudderStack(marTechEnabled);

    useEffect(() => {
        if (!isRudderStackReady || !userId) {
            return;
        }
        cacheMarketingIdentity();
    }, [isRudderStackReady, userId]);
};

/**
 * Hook that runs the initializations for the apps home screen.
 *
 * This includes checking app version updates, setting up biometric checks, initializing the geocomply client, etc.
 */
export const useAppInit = () => {
    const product = useJurisdictionStore(store => store.product) ?? '';
    const jurisdiction = useJurisdictionStore(store => store.jurisdiction) ?? '';
    const refetchSettings = useJurisdictionStore(store => store.actions.refetchAndUpdateSettings);
    const profile = useUserStore(state => state.profile);

    const [geocomplyClientReady, setGeocomplyClientReady] = useState(false);

    const { data: userGlobalId } = useAuthUserConfig({ select: userIdSelector });

    useEffect(() => {
        if (userGlobalId) {
            BetrAnalytics.setUserProperties({ [AnalyticsUserProperty.CUSTOM_USER_GLOBAL_ID]: userGlobalId });
            Segmentation.setUserBucketBasedOnUserId(userGlobalId);
        }
    }, [userGlobalId]);

    useEffect(() => {
        if (!profile.sub) {
            return;
        }
        BetrAnalytics.setUserId(profile.sub);
        BetrAnalytics.setUserProperties({ [AnalyticsUserProperty.CUSTOM_USER_ID]: profile.sub });
    }, [profile.sub]);

    useEffect(() => {
        //Request push notification permission when user opens the lobby but with a delay
        //Since we are requesting provisional permission, no prompt will be shown on iOS,
        //but a permission prompt will be shown on Android
        setTimeout(() => {
            requestNotificationPermission();
        }, 5000);
    }, []);

    useAppVersionCheck();

    usePromptBiometricGuard();

    useBiometricCheck();

    useMaintenanceCheck();

    useMakeFirstDeposit();

    useAppResumeEffect(
        useCallback(() => {
            refetchSettings();
        }, [refetchSettings])
    );

    useUpdatePickemLeagueConfigs();

    useFetchAndActivateFirebaseRemoteConfigs();

    useGuestCacheInvalidation();

    useEffect(() => {
        try {
            if (!useUserStore.getState().guest) {
                //Only intialize geocomply client if user logged in
                getGeocomplyClient({ product: product, jurisdiction: jurisdiction })
                    .init({ userId: profile.sub })
                    .then(() => {
                        logger.debug('[App Init] Geocomply client initialized');
                        setGeocomplyClientReady(true);
                    });
            }
        } catch (err) {
            logger.warn('[App Init]', err);
        }
    }, [product, jurisdiction, profile.sub]);

    return { geocomplyClientReady };
};
